const DRIVING_CACHE = new Map();

const DISTANCE_API = "/api/meet-driving-distance";
const STATIC_DISTANCES_URL = "/meet-visit-distances.json";

let staticDistancesPromise = null;

export const formatDrivingMilesFromVenue = (miles) => {
  if (miles == null || Number.isNaN(miles)) return "";
  if (miles < 0.15) return "At the venue";
  const rounded = miles < 10 ? miles.toFixed(1) : String(Math.round(miles));
  return `≈ ${rounded} mi drive from venue`;
};

/** @deprecated Use formatDrivingMilesFromVenue */
export const formatMilesFromVenue = formatDrivingMilesFromVenue;

export const buildVenueAddressQuery = (venue = {}) => {
  const parts = [venue.street, venue.cityStateZip].filter(Boolean);
  return parts.join(", ").replace(/\s+/g, " ").trim();
};

export const buildVenueGeocodeFallbacks = (venue = {}) => {
  const name = venue.name?.trim();
  const cityStateZip = venue.cityStateZip?.trim();
  if (!name || !cityStateZip) return [];
  return [`${name}, ${cityStateZip}`.replace(/\s+/g, " ").trim()];
};

export const buildDistanceLookupKey = (venueQuery, placeQuery) => {
  const venue = String(venueQuery || "").trim().replace(/\s+/g, " ");
  const place = placeQuery?.includes("KY")
    ? String(placeQuery).trim().replace(/\s+/g, " ")
    : `${String(placeQuery || "").trim().replace(/\s+/g, " ")}, KY`;
  return `${venue}|${place}`;
};

const loadStaticDistances = async () => {
  if (staticDistancesPromise) return staticDistancesPromise;

  staticDistancesPromise = fetch(STATIC_DISTANCES_URL)
    .then((response) => (response.ok ? response.json() : { distances: {} }))
    .catch(() => ({ distances: {} }));

  return staticDistancesPromise;
};

const lookupStaticDistance = (staticData, venueQuery, placeQuery) => {
  const key = buildDistanceLookupKey(venueQuery, placeQuery);
  const hit = staticData?.distances?.[key];
  if (!hit?.label) return null;
  return {
    miles: typeof hit.miles === "number" ? hit.miles : null,
    label: hit.label,
  };
};

export const buildDirectionsUrl = (origin, destination) => {
  if (!origin || !destination) return "";
  const params = new URLSearchParams({
    api: "1",
    origin,
    destination,
  });
  return `https://www.google.com/maps/dir/?${params}`;
};

/** Apple Maps — opens the Maps app on iPhone, iPad, and Safari on Mac. */
export const buildAppleMapsDirectionsUrl = (origin, destination) => {
  if (!destination) return "";
  const params = new URLSearchParams({ daddr: destination, dirflg: "d" });
  if (origin) params.set("saddr", origin);
  return `https://maps.apple.com/?${params.toString()}`;
};

/** True for iOS and Safari on macOS — use Apple Maps deep links. */
export const prefersAppleMaps = (userAgent = "") => {
  const ua =
    userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (
    /Macintosh/.test(ua) &&
    /Safari/.test(ua) &&
    !/Chrome|Chromium|Edg|OPR|Firefox/.test(ua)
  ) {
    return true;
  }
  return false;
};

/** Google Maps on Android/desktop; Apple Maps on iOS and Safari on Mac. */
export const buildNativeMapsDirectionsUrl = (origin, destination, userAgent) => {
  if (!destination) return "";
  if (prefersAppleMaps(userAgent)) {
    return buildAppleMapsDirectionsUrl(origin, destination);
  }
  if (origin) return buildDirectionsUrl(origin, destination);
  const params = new URLSearchParams({ api: "1", destination });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const drivingCacheKey = (venueQuery, placeQuery) => `${venueQuery}|${placeQuery}`;

const fetchDrivingDistanceFromApi = async (venueQuery, placeQuery, venueFallbacks = []) => {
  const cacheKey = `${drivingCacheKey(venueQuery, placeQuery)}|${venueFallbacks.join(";")}`;
  if (DRIVING_CACHE.has(cacheKey)) return DRIVING_CACHE.get(cacheKey);

  const params = new URLSearchParams({ venue: venueQuery, place: placeQuery });
  venueFallbacks.forEach((fallback) => {
    if (fallback) params.append("venueFallback", fallback);
  });
  const response = await fetch(`${DISTANCE_API}?${params}`);
  if (!response.ok) return null;

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;

  const data = await response.json();
  const result = {
    miles: typeof data.miles === "number" ? data.miles : null,
    label: data.label || formatDrivingMilesFromVenue(data.miles),
  };
  DRIVING_CACHE.set(cacheKey, result);
  return result;
};

export const resolveDrivingDistance = async (venueQuery, placeQuery, staticData = null, venueFallbacks = []) => {
  if (!venueQuery || !placeQuery) return null;

  const placeWithRegion = placeQuery.includes("KY") ? placeQuery : `${placeQuery}, KY`;
  const staticDistances = staticData || (await loadStaticDistances());
  const fromStatic = lookupStaticDistance(staticDistances, venueQuery, placeWithRegion);
  if (fromStatic?.label) return fromStatic;

  try {
    const fromApi = await fetchDrivingDistanceFromApi(
      venueQuery,
      placeWithRegion,
      venueFallbacks
    );
    if (fromApi?.label) return fromApi;
  } catch {
    // Netlify function unavailable — static JSON is the primary source at runtime.
  }

  return null;
};

const resolvePlaceDistance = async (place, venueQuery, staticData, venueFallbacks) => {
  if (place.distance?.trim()) return place;

  const address = place.address?.trim();
  if (!address || !venueQuery) return place;

  const result = await resolveDrivingDistance(
    venueQuery,
    address,
    staticData,
    venueFallbacks
  );

  return {
    ...place,
    distance: result?.label || "",
    directionsUrl:
      place.directionsUrl ||
      buildDirectionsUrl(venueQuery, address.includes("KY") ? address : `${address}, KY`),
  };
};

const resolvePlaceList = async (places = [], venueQuery, staticData, venueFallbacks) => {
  const resolved = [];

  for (const place of places) {
    if (place.distance?.trim() || !place.address?.trim()) {
      resolved.push({
        ...place,
        directionsUrl:
          place.directionsUrl ||
          buildDirectionsUrl(
            venueQuery,
            place.address?.includes("KY") ? place.address : `${place.address}, KY`
          ),
      });
      continue;
    }

    resolved.push(await resolvePlaceDistance(place, venueQuery, staticData, venueFallbacks));
  }

  return resolved;
};

const attachDirectionsOnly = (places = [], venueQuery) =>
  (places || []).map((place) => {
    const address = place.address?.trim();
    const destination = address
      ? address.includes("KY")
        ? address
        : `${address}, KY`
      : "";

    return {
      ...place,
      distance: "",
      directionsUrl:
        place.directionsUrl ||
        (destination ? buildDirectionsUrl(venueQuery, destination) : ""),
    };
  });

export const enrichVisitDistances = async (visit, venue) => {
  if (!visit) return visit;

  const venueQuery = buildVenueAddressQuery(venue);
  if (!venueQuery) return visit;

  const venueFallbacks = buildVenueGeocodeFallbacks(venue);
  const staticData = await loadStaticDistances();
  const hotels = await resolvePlaceList(visit.hotels, venueQuery, staticData, venueFallbacks);
  const restaurants = attachDirectionsOnly(visit.restaurants, venueQuery);

  return { ...visit, hotels, restaurants };
};
